/**
 * Minimal shape of the Looker `run_inline_query` body — inlined from
 * `@looker/sdk` to keep the dependency out of the bundle.
 */
interface LookerInlineQueryBody {
  fields?: string[] | null;
  filters?: Record<string, string> | null;
  filter_expression?: string | null;
  sorts?: string[] | null;
  limit?: string | null;
  column_limit?: string | null;
  total?: boolean | null;
  row_total?: string | null;
  subtotals?: string[] | null;
  pivots?: string[] | null;
  fill_fields?: string[] | null;
  dynamic_fields?: string | null;
  query_timezone?: string | null;
  vis_config?: Record<string, unknown> | null;
  client_id?: string | null;
}

type LookerResultFormat =
  | 'json'
  | 'json_detail'
  | 'json_bi'
  | 'csv'
  | 'txt'
  | 'html'
  | 'md'
  | 'xlsx'
  | 'sql'
  | 'png'
  | 'jpg';

export enum EmbedAccessActions {
  GRANT = 'grant',
  REVOKE = 'revoke',
}

export interface AlterEmbedAccessRequest {
  userID: string;
  action: EmbedAccessActions;
  access: string;
}

export interface AlterEmbedAccessResponse {
  eventID: string;
  response: {
    embedUserConfirmed: boolean;
    groupID: number;
    message: string;
  };
}

export interface GetDashboardLookMLRequest {
  objectList?: string[];
  source?: 'gcs' | 'looker';
  slug?: string;
}

export interface GetDashboardLookMLResponse {
  fileContents: string[];
}

export interface ImportDashboardFromLookMLRequest {
  objectsHash?: string;
  lookml?: string;
  folderId?: string;
}

export interface ExecuteLookerQueryRequest extends Partial<LookerInlineQueryBody> {
  lookml_model_name?: string;
  explore_name?: string;
  result_format?: LookerResultFormat;
}

export interface ExecuteLookerQueryResponse<T> {
  eventID: string;
  result_format: string;
  query_id: string;
  slug: string;
  share_url: string;
  sql: string;
  query_result: T;
}

export interface GetLookerSuggestionsRequest {
  lookml_model_name?: string;
  explore_name?: string;
  dimension_name?: string;
  filters?: string;
  pseudo_filters?: string;
  term?: string;
}

export interface GetLookerSuggestionsResponse {
  eventID: string;
  result: {
    dimension_name: string;
    suggestions: string[];
    hit_limit: boolean;
    from_cache: boolean;
    used_calcite_materialization: boolean;
    error: string;
    source: string;
  };
}

// ---------------------------------------------------------------------------
// Backend-proxy DTOs for /v7/looker/*.
//
// The FE never talks to Looker directly for the endpoints below — everything
// is proxied by bi-api with the Looker service token stored server-side.
// ---------------------------------------------------------------------------

/**
 * Local shape for a Looker dashboard filter, decoupled from `@looker/sdk`.
 * Backend returns this subset via `GET /v7/looker/dashboards/{slug}/filters`.
 */
export interface DashboardFilter {
  name: string;
  type: string;
  default_value?: string;
  ui_config?: { type?: string; display?: string; options?: string[] } | null;
  model?: string;
  explore?: string;
  field?: { name?: string; default_filter_value?: string } | null;
  allow_multiple_values?: boolean;
  required?: boolean;
}

/** Envelope returned by `GET /v7/looker/dashboards/{slug}/filters`. */
export interface DashboardFiltersResponse {
  response: DashboardFilter[];
  eventID: string;
}

/** Response of `GET /v7/looker/user/me`. */
export interface LookerUser {
  id: number;
  email: string;
  personalFolderId?: string;
  firstName?: string;
  lastName?: string;
  displayName?: string;
}

/**
 * Request for `POST /v7/looker/embed/acquire_session`.
 *
 * The JWT only carries `sub` (accountId) + `aud`, so the backend cannot
 * derive user identity on its own. The FE passes everything it knows from
 * `window.LOOKER_CONFIG` so the backend can call
 * `sdk.acquire_embed_cookieless_session` with the correct params.
 *
 * Fields prefixed with `// (legacy acquire_session)` mirror the
 * `requestParams` block the Angular host used to send directly to
 * `POST /v4/cookieless/acquire_session` before extensionless. They are
 * forwarded verbatim to Looker when supplied by the host via
 * `window.LOOKER_CONFIG.embedSession`.
 */
export interface AcquireCookielessSessionRequest {
  // ── User identity (from window.LOOKER_CONFIG.user) ──────────────────────
  /** Looker user ID — used as `external_user_id` on the embed session. */
  lookerUserId?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  /** Account ID — forwarded as `user_attributes.accountid`. */
  accountId?: string;

  // ── Session context ──────────────────────────────────────────────────────
  /**
   * Origin of the embedding page. Looker validates this against its
   * Embedded Domain Allowlist. Populated from `window.location.origin` or
   * from `window.LOOKER_CONFIG.embedSession.embedDomain`.
   */
  embedDomain?: string;
  /**
   * Portal session ID from `window.LOOKER_CONFIG.platformSessionId`.
   * Backend forwards as `user_attributes.platform_session_id`.
   */
  platformSessionId?: string;

  // ── Workspace context (set via setCookielessSessionContext) ──────────────
  /** Scopes the Looker session to a workspace type's model set. */
  workspaceTypeId?: string;
  /** Forwarded as `user_attributes.advertiserid`. */
  advertiserId?: string;

  // ── Legacy acquire_session overrides (from LOOKER_CONFIG.embedSession) ──
  /** (legacy acquire_session) Force Looker to invalidate any prior embed session. */
  forceLogoutLogin?: boolean;
  /** (legacy acquire_session) LookML models the embed session is allowed to read. */
  models?: string[];
  /** (legacy acquire_session) Looker embed permissions for this session. */
  permissions?: string[];
  /**
   * (legacy acquire_session) Extra Looker `user_attributes` merged with the
   * ones the backend derives from the authenticated user.
   */
  userAttributes?: Record<string, string | number | boolean | null>;
  /** (legacy acquire_session) Session length in seconds. */
  sessionLength?: number;
}

export interface CookielessSessionTokens {
  authentication_token: string;
  authentication_token_ttl: number;
  navigation_token: string;
  navigation_token_ttl: number;
  session_reference_token: string;
  session_reference_token_ttl: number;
  api_token?: string;
  api_token_ttl?: number;
}

export type AcquireCookielessSessionResponse = CookielessSessionTokens;

export interface GenerateCookielessTokensRequest {
  session_reference_token?: string;
  navigation_token?: string;
  api_token?: string;
}

export type GenerateCookielessTokensResponse = CookielessSessionTokens;

/** Body for `POST /v7/looker/dashboards/import_lookml`. */
export interface ImportDashboardLookMLBackendRequest {
  lookml: string;
  folderId?: string;
}

/**
 * Response payload of `POST /v7/looker/dashboards/import_lookml`.
 *
 * Backends have historically shipped this three different ways — all three
 * are accepted and normalized in `importDashboardFromLookML`:
 *   { dashboardId }                                    // FE-preferred flat shape
 *   { dashboard_id }                                   // Looker SDK passthrough
 *   { response: { dashboardId | dashboard_id }, eventID } // bi-api envelope
 */
export interface ImportDashboardLookMLBackendResponse {
  dashboardId?: string;
  dashboard_id?: string;
  response?: { dashboardId?: string; dashboard_id?: string };
  eventID?: string;
}
